<template>
  <div
    :class="{ compressed }"
    class="skin-card"
  >
    <NuxtLink
      :aria-label="`Link to skin${item.localed_name}`"
      :to="link"
      class="skin-link"
    />

    <Cases
      :cases="item.cases as string[] || []"
      :compressed="compressed"
    />

    <div class="skin-image">
      <Thumb
        :class="{ scaled: vanillaKnifes.includes(getModifiedString(item.market_hash_name)) }"
        :font-size="16"
        :height="15"
        :item="item"
        :size="16"
      />
    </div>
    <div class="skin-info">
      <div
        v-if="item.collection"
        class="skin-collection"
      >
        <img
          :alt="item.collection"
          :src="getImageFromWaxpeer(item.collection, '50:50')"
          class="collection-img"
          loading="lazy"
        >
        <span
          v-tooltip="item.collection >= 27 ? item.collection : ''"
          class="collection-name"
        >
          {{ item.collection }}
        </span>
      </div>

      <div class="f fb">
        <h2 class="f fcol fs ft">
          <!--  не трогать skin-brand, появляется лишний пробел -->
          <span class="skin-brand">{{ getSkinBrand(item.market_hash_name) }}&nbsp;</span>
          <span class="skin-name mb0">
            {{ item.skin || getMarketName(item.localed_name) }}
          </span>
        </h2>
        <span
          v-if="item.phase"
          :class="getModifiedString(item.phase)"
        >
          {{ item.phase }}
        </span>
      </div>
    </div>

    <template
      v-if="item.is_music_kit_box && getModifiedString(item.market_hash_name).includes('stattrak')"
    >
      <div class="separator" />
      <div class="skin-include-stattrak">
        {{ $t('include_stattrak_items') }}
      </div>
    </template>

    <div class="separator" />

    <!--    <client-only> -->
    <PagesWeaponComponentsPrices :item="item" />
    <!--    </client-only> -->

    <div class="separator last" />

    <!--    <client-only> -->
    <div
      v-if="item.offers"
      class="skin-offers f fc gap8"
    >
      <span>
        <b>{{ t("offers_count", { count: getOffersCount(item.offers) }) }}</b>
      </span>
      <span> {{ t("offers_on_markets", { count: item.markets_with_offers }) }}</span>
    </div>
    <div
      v-else
      class="skin-offers"
    >
      {{ t('No offers') }}
    </div>
    <!--    </client-only> -->
  </div>
</template>

<script lang="ts" setup>
import type { ItemsByField } from '~/graphql/generated/schema-types';
import { useI18n } from 'vue-i18n';
import Thumb from '~/components/Pages/Item/Components/Children/Thumb.vue';
import Cases from '~/components/Pages/Weapon/Components/Cases.vue';
import { useCompressed } from '~/composables/items-compressed';
import { getImageFromWaxpeer } from '~/utils/get-image-url';

const props = defineProps<{
  item: ItemsByField
  link: string
}>();

const { t } = useI18n();
const { compressed } = useCompressed();
const vanillaKnifes = [
  'skeleton-knife',
  'paracord-knife',
  'nomad-knife',
  // 'kukri-knife',
  'karambit',
  'gut-knife',
  'falchion-knife',
  'classic-knife',
  'butterfly-knife',
  'bayonet',
  'bowie-knife',
];

function getSkinBrand(name: string) {
  if (props.item.is_music_kit_box) {
    return 'Music Kit Box';
  }
  return name.replace(/ \| .*/, '');
}

function getMarketName(name: string): string {
  if (props.item.is_music_kit_box) {
    return name.replace('StatTrak™', '').replace('Music Kit Box', '');
  }
  // eslint-disable-next-line regexp/no-super-linear-backtracking
  const match = name.match(/\|\s*([^(|\s][^(|]*?)\s*(\(|$)/);
  return match ? match[1].trim() : '';
}

function getOffersCount(num: number): string {
  if (num < 1000) {
    return num.toString();
  }
  if (num < 1000000) {
    return `${Intl.NumberFormat('en', { notation: 'compact' }).format(num / 1000)}K`;
  }
  return `${Intl.NumberFormat('en', { notation: 'compact' }).format(num / 1000000)}M`;
}
</script>

<style lang="scss" scoped>
@use 'sass:color';

.skin-card {
  padding: 0.8rem 0.8rem 1.2rem;
  background: var(--surfaces-low-container);
  border-radius: 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  position: relative;

  &:hover,
  &:focus {
    outline: none;
    background: color-mix(in srgb, var(--surfaces-low-container) 98%, white);
  }

  .skin-include-stattrak {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--stattrak);
    padding: 4px 20px;
    font-size: 16px;
    font-weight: 600;
    //text-shadow: 0 0 6px var(--stattrak);
  }

  .skin-link {
    width: 100%;
    position: absolute;
    inset: 0;
    z-index: 1;
  }

  .skin-info {
    margin: auto 0 0.8rem;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;

    .skin-collection {
      display: flex;
      align-items: center;
      position: relative;
      gap: 8px;

      .collection-img, &:deep(.collection-img) {
        width: 3rem;
        height: 2.5rem;
      }

      .collection-name {
        font-weight: 600;
        font-size: 1.4rem;
        line-height: 1.9rem;
        color: var(--accent-secondary);
      }
    }

    .skin-name {
      font-weight: 800;
      font-size: 1.8rem;
      line-height: 2.5rem;
      color: var(--surfaces-on-surface);
      margin: 0.8rem 0;
    }

    .skin-brand {
      display: inline-block;
      font-weight: 500;
      font-size: 1.4rem;
      line-height: 1.9rem;
      color: var(--surfaces-on-surface-variable);
    }
  }

  &:deep(.prices-container) {
    margin: 0;
  }

  .separator {
    width: 100%;
    height: 1px;
    background: var(--surfaces-high-container);
  }

  .skin-offers {
    font-size: 16px;
    text-align: center;
  }

  .scaled:deep(img) {
    object-fit: cover;
  }
}

@media screen and (max-width: 768px) {
  .skin-card.compressed {
    padding: 6px 6px 12px;
    overflow: hidden;

    .skin-image:deep(.thumb) {
      padding: 0;
    }

    .skin-info {
      margin: auto 4px 0;

      .skin-brand {
        font-size: 13px;
      }

      .skin-name {
        font-size: 14px;
        margin: 0;
      }

      .skin-collection {
        img {
          width: 24px;
          height: 24px;
        }

        .collection-name {
          font-size: 12px;
        }
      }
    }

    .separator.last {
      display: none;
    }

    &:deep(.prices-container) {
      margin: 0;

      .skin-prices {
        gap: 8px;

        .price-range {
          gap: 5px;

          .prices {
            width: 100%;
            justify-content: center;

            a {
              p, span .price, .price p, .price span {
                font-size: 14px;
              }
            }
          }
        }
      }
    }

    .skin-offers {
      display: none;
    }
  }
}
</style>
